import IWidget from "./iwidget";
import { WidgetIndex } from "./widgetIndex";

type ShowIf = {
    field: string;
    op: string;
    value: any;
}

type Observable = {
    observedWidget: IWidget;
    toggleableWidget: IWidget;
    showIf: ShowIf;
}

/**
 * The WidgetToggler parses the `@ShowIf` annotations and hides/shows fields depending on another field’s value.
 * `WidgetIndex` calls `observe()` for all widgets and `process()` after that.
 */
export class WidgetToggler {

    index: WidgetIndex;
    observables: Observable[] = [];

    constructor(index: WidgetIndex) {
        this.index = index;
    }

    observe(w: IWidget): void {
        const showIf = WidgetToggler.parseShowIf(w);

        if (showIf === null) return;

        const field = this.index.findWidget(showIf.field, w.lang);
        if (field === false) {
            console.log('WidgetToggler: could not find widget', field);
            return;
        }

        this.observables.push({
            observedWidget: field,
            toggleableWidget: w,
            showIf
        });
    }

    process(): void {
        this.observables.forEach(o => {
            o.observedWidget.listen('change', () => WidgetToggler.handleChange(o));

            // execute once
            WidgetToggler.handleChange(o);
        })
    }

    static async handleChange(observable: Observable): Promise<void> {
        const regex = typeof observable.showIf.value === 'object' && typeof observable.showIf.value.test === 'function';
        const val = await observable.observedWidget.getValue();
        let show = false;

        // equal
        if (observable.showIf.op == '==') {
            show = val == observable.showIf.value;
        }

        // starts with
        else if (observable.showIf.op == '^=') {
            show = val.startsWith(observable.showIf.value);
        }

        // ends with
        else if (observable.showIf.op == '$=') {
            show = val.endsWith(observable.showIf.value);
        }

        // contains
        else if (observable.showIf.op == '*=') {
            show = val.contains(observable.showIf.value);
        }

        // matches
        else if (observable.showIf.op == 'matches' && regex) {
            show = observable.showIf.value.test(val);
        }

        observable.toggleableWidget.$field.classList.toggle('is-hidden', !show);
    }

    static parseShowIf(w: IWidget): ShowIf {
        const condition = w.showIf;
        if (typeof condition === 'undefined') return null;

        let [field, op, value]: any = condition.split(' ');
        if (value === 'true') value = true;
        if (value === 'false') value = false;
        if (op == 'matches') {
            try {
                value = new RegExp(value);
            } catch (e: any) {
                console.warn('invalid regex: ' + value);
            }
        }

        return { field, op, value };
    }
}
